<template>
  <div class="copilot">
    <!-- Sentimento & intenção -->
    <section class="cp-card">
      <header class="cp-card__head">
        <svg class="cp-ic cp-ic--ai" viewBox="0 0 24 24">
          <path
            d="M12 3 13.9 8.6 20 9.2 15.5 13.1 16.8 19 12 15.8 7.2 19 8.5 13.1 4 9.2 10.1 8.6 12 3Z"
          />
        </svg>
        <h4>{{ $t('CONVERSATION_SIDEBAR.COPILOT.SENTIMENT_TITLE') }}</h4>
        <span v-if="liveSentiment" class="cp-live" :title="$t('CONVERSATION_SIDEBAR.COPILOT.LIVE_HINT')">
          <span class="cp-live__dot"></span>
          {{ $t('CONVERSATION_SIDEBAR.COPILOT.LIVE') }}
        </span>
      </header>

      <!-- Medidor de sentimento AO VIVO (nativo, grátis, automático) -->
      <div v-if="insightsLoading && !liveSentiment" class="cp-skeleton">
        <span></span><span></span>
      </div>
      <div v-else-if="liveSentiment" class="cp-sent">
        <div class="cp-sent__badges">
          <span
            class="cp-badge"
            :class="`cp-badge--${liveSentiment.sentiment}`"
          >
            {{
              $t(
                `CONVERSATION_SIDEBAR.COPILOT.SENTIMENT_${liveSentiment.sentiment.toUpperCase()}`
              )
            }}
          </span>
        </div>
        <div
          class="cp-meter"
          role="meter"
          :aria-valuenow="liveSentiment.score"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="cp-meter__fill"
            :class="`cp-meter__fill--${liveSentiment.sentiment}`"
            :style="{ width: `${liveSentiment.score}%` }"
          />
        </div>
      </div>
      <p v-else class="cp-card__hint">
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.SENTIMENT_HINT') }}
      </p>

      <!-- Detalhes: intenção, tags e urgência (DeepSeek, sob demanda/pago) -->
      <div v-if="detailsLoading" class="cp-skeleton cp-skeleton--details">
        <span></span><span></span>
      </div>
      <div v-else-if="sentimentDetails" class="cp-sent cp-sent--details">
        <span
          class="cp-badge"
          :class="`cp-badge--urg-${sentimentDetails.urgency}`"
        >
          {{ $t('CONVERSATION_SIDEBAR.COPILOT.URGENCY') }}:
          {{
            $t(
              `CONVERSATION_SIDEBAR.COPILOT.URGENCY_${sentimentDetails.urgency.toUpperCase()}`
            )
          }}
        </span>
        <p v-if="sentimentDetails.intent" class="cp-sent__intent">
          <strong>{{ $t('CONVERSATION_SIDEBAR.COPILOT.INTENT') }}:</strong>
          {{ sentimentDetails.intent }}
        </p>
        <ul v-if="sentimentDetails.tags.length" class="cp-tags">
          <li v-for="(tag, i) in sentimentDetails.tags" :key="i" class="cp-tag">
            {{ tag }}
          </li>
        </ul>
        <p
          v-if="staleFlags.sentiment && !isCoolingDown('sentiment')"
          class="cp-stale"
          @click="generateSentimentDetails(true)"
        >
          {{ $t('CONVERSATION_SIDEBAR.COPILOT.STALE') }}
        </p>
        <p v-if="isCoolingDown('sentiment')" class="cp-cooldown">
          {{ $t('CONVERSATION_SIDEBAR.COPILOT.COOLDOWN', { min: cooldownMin('sentiment') }) }}
        </p>
      </div>
      <button
        v-else-if="!insightsLoading"
        type="button"
        class="cp-btn cp-btn--ghost"
        @click="generateSentimentDetails()"
      >
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.DETAILS_CTA') }}
      </button>
    </section>

    <!-- Resumo da conversa -->
    <section class="cp-card">
      <header class="cp-card__head">
        <svg class="cp-ic" viewBox="0 0 24 24">
          <path d="M4 6h16M4 12h10M4 18h7" />
        </svg>
        <h4>{{ $t('CONVERSATION_SIDEBAR.COPILOT.SUMMARY_TITLE') }}</h4>
        <button
          v-if="summary && !summaryLoading"
          type="button"
          class="cp-refresh"
          :class="{ 'cp-refresh--off': isCoolingDown('summarize') }"
          :disabled="isCoolingDown('summarize')"
          :title="$t('CONVERSATION_SIDEBAR.COPILOT.REFRESH')"
          @click="generateSummary(true)"
        >
          <svg class="cp-ic cp-ic--sm" viewBox="0 0 24 24">
            <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />
          </svg>
        </button>
      </header>

      <div v-if="summaryLoading || insightsLoading" class="cp-skeleton">
        <span></span><span></span><span></span>
      </div>
      <p v-else-if="!summary" class="cp-card__hint">
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.SUMMARY_HINT') }}
      </p>
      <div v-else class="cp-summary" v-html="renderedSummary" />

      <p
        v-if="staleFlags.summarize && summary && !summaryLoading && !isCoolingDown('summarize')"
        class="cp-stale"
        @click="generateSummary(true)"
      >
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.STALE') }}
      </p>
      <p v-if="summary && isCoolingDown('summarize')" class="cp-cooldown">
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.COOLDOWN', { min: cooldownMin('summarize') }) }}
      </p>

      <button
        v-if="!summary && !summaryLoading && !insightsLoading"
        type="button"
        class="cp-btn"
        @click="generateSummary()"
      >
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.SUMMARY_CTA') }}
      </button>
    </section>

    <!-- Respostas sugeridas -->
    <section class="cp-card">
      <header class="cp-card__head">
        <svg class="cp-ic" viewBox="0 0 24 24">
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
          />
        </svg>
        <h4>{{ $t('CONVERSATION_SIDEBAR.COPILOT.SUGGEST_TITLE') }}</h4>
        <button
          v-if="suggestions.length && !suggestLoading"
          type="button"
          class="cp-refresh"
          :class="{ 'cp-refresh--off': isCoolingDown('reply_suggestion') }"
          :disabled="isCoolingDown('reply_suggestion')"
          :title="$t('CONVERSATION_SIDEBAR.COPILOT.REFRESH')"
          @click="generateSuggestions(true)"
        >
          <svg class="cp-ic cp-ic--sm" viewBox="0 0 24 24">
            <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />
          </svg>
        </button>
      </header>

      <div v-if="suggestLoading || insightsLoading" class="cp-skeleton">
        <span></span><span></span>
      </div>
      <p v-else-if="!suggestions.length" class="cp-card__hint">
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.SUGGEST_HINT') }}
      </p>
      <ul v-else class="cp-suggestions">
        <li v-for="(text, i) in suggestions" :key="i" class="cp-sug">
          <p>{{ text }}</p>
          <div class="cp-sug__actions">
            <button
              type="button"
              class="cp-chip cp-chip--primary"
              @click="useSuggestion(text)"
            >
              {{ $t('CONVERSATION_SIDEBAR.COPILOT.USE') }}
            </button>
            <button
              type="button"
              class="cp-chip"
              @click="copySuggestion(text)"
            >
              {{ $t('CONVERSATION_SIDEBAR.COPILOT.COPY') }}
            </button>
          </div>
        </li>
      </ul>

      <p
        v-if="staleFlags.reply_suggestion && suggestions.length && !suggestLoading && !isCoolingDown('reply_suggestion')"
        class="cp-stale"
        @click="generateSuggestions(true)"
      >
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.STALE') }}
      </p>
      <p v-if="suggestions.length && isCoolingDown('reply_suggestion')" class="cp-cooldown">
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.COOLDOWN', { min: cooldownMin('reply_suggestion') }) }}
      </p>

      <button
        v-if="!suggestions.length && !suggestLoading && !insightsLoading"
        type="button"
        class="cp-btn"
        @click="generateSuggestions()"
      >
        {{ $t('CONVERSATION_SIDEBAR.COPILOT.SUGGEST_CTA') }}
      </button>
    </section>

    <p v-if="unlimited" class="cp-unlimited">
      {{ $t('CONVERSATION_SIDEBAR.COPILOT.UNLIMITED') }}
    </p>

    <footer class="cp-foot">
      <svg class="cp-ic cp-ic--sm cp-ic--ai" viewBox="0 0 24 24">
        <path
          d="M12 3 13.9 8.6 20 9.2 15.5 13.1 16.8 19 12 15.8 7.2 19 8.5 13.1 4 9.2 10.1 8.6 12 3Z"
        />
      </svg>
      {{ $t('CONVERSATION_SIDEBAR.COPILOT.DISCLAIMER') }}
    </footer>
  </div>
</template>

<script>
import { BUS_EVENTS } from 'shared/constants/busEvents';
import alertMixin from 'shared/mixins/alertMixin';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import CopilotAPI from 'dashboard/api/copilot';

export default {
  mixins: [alertMixin, messageFormatterMixin],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    // Id da última mensagem real — muda quando chega mensagem nova (reativo).
    // Dispara a atualização do sentimento ao vivo.
    lastMessageId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      // Detalhes sob demanda (DeepSeek, pago): urgência, intenção, tags.
      sentimentDetails: null,
      detailsLoading: false,
      summary: '',
      summaryLoading: false,
      suggestions: [],
      suggestLoading: false,
      // Carregando o cache (hidratação): enquanto true, escondemos os botões de
      // gerar para não piscarem clicáveis antes de sabermos se já há conteúdo.
      insightsLoading: true,
      // Marca, por evento, se o insight em cache ficou desatualizado
      // (mensagens novas entraram desde a geração).
      staleFlags: {},
      // debounce da atualização ao vivo (evita chamar a cada mensagem em rajada).
      liveTimer: null,
      // Cooldown por evento pago (timestamp em ms até liberar) — vem do backend.
      cooldowns: {},
      // Conta usa a própria integração OpenAI → sem cooldown/limite (ilimitado).
      unlimited: false,
      // Relógio que "tica" para atualizar o contador de cooldown na tela.
      nowTs: Date.now(),
      clockTimer: null,
    };
  },
  computed: {
    renderedSummary() {
      return this.summary ? this.formatMessage(this.summary) : '';
    },
    // Sentimento ao vivo vem da fonte única no store (mesma do chip no
    // compositor) — uma só chamada nativa, valores sempre iguais.
    liveSentiment() {
      return this.$store.getters['copilot/getLiveSentiment'](
        this.conversationId
      );
    },
  },
  watch: {
    // Ao trocar de conversa, limpa o resultado e recarrega o cache.
    conversationId() {
      this.resetState();
      this.loadInsights();
    },
    // Mensagem nova na conversa → atualiza o sentimento ao vivo (grátis) e
    // marca os detalhes (pagos) como desatualizados, sem regerá-los sozinho.
    lastMessageId(newId, oldId) {
      if (!newId || newId === oldId) return;
      if (this.sentimentDetails) this.$set(this.staleFlags, 'sentiment', true);
      this.scheduleLiveRefresh();
    },
  },
  mounted() {
    this.loadInsights();
    // Atualiza o contador de cooldown a cada 15s (granularidade em minutos).
    this.clockTimer = setInterval(() => {
      this.nowTs = Date.now();
    }, 15000);
  },
  beforeDestroy() {
    if (this.liveTimer) clearTimeout(this.liveTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
  },
  methods: {
    resetState() {
      this.sentimentDetails = null;
      this.summary = '';
      this.suggestions = [];
      this.staleFlags = {};
      this.cooldowns = {};
      if (this.liveTimer) clearTimeout(this.liveTimer);
    },
    // --- Cooldown (só modo global/pago; ilimitado quando integração própria) ---
    // Recebe SEGUNDOS restantes (do backend) e ancora no relógio local, para
    // não depender de sincronia de horário entre servidor e navegador.
    setCooldown(event, seconds) {
      const ts = seconds ? Date.now() + seconds * 1000 : 0;
      this.$set(this.cooldowns, event, ts);
    },
    isCoolingDown(event) {
      return (this.cooldowns[event] || 0) > this.nowTs;
    },
    cooldownMin(event) {
      const ms = (this.cooldowns[event] || 0) - this.nowTs;
      return ms > 0 ? Math.ceil(ms / 60000) : 0;
    },
    // Hidrata os cards PAGOS (detalhes/resumo/sugestões) do cache, de graça.
    // O sentimento ao vivo é responsabilidade do store (fonte única).
    async loadInsights() {
      this.insightsLoading = true;
      try {
        const { data } = await CopilotAPI.getInsights(this.conversationId);
        const insights = data.insights || {};
        const flags = {};
        this.unlimited = !!data.unlimited;

        const details = insights.sentiment;
        if (details && typeof details.message === 'object') {
          this.sentimentDetails = details.message;
          flags.sentiment = !!details.stale;
          this.setCooldown('sentiment', details.cooldown_seconds);
        }
        const summarize = insights.summarize;
        if (summarize && typeof summarize.message === 'string') {
          this.summary = summarize.message;
          flags.summarize = !!summarize.stale;
          this.setCooldown('summarize', summarize.cooldown_seconds);
        }
        const reply = insights.reply_suggestion;
        if (reply && Array.isArray(reply.message)) {
          this.suggestions = reply.message;
          flags.reply_suggestion = !!reply.stale;
          this.setCooldown('reply_suggestion', reply.cooldown_seconds);
        }
        this.staleFlags = flags;
      } catch (error) {
        // cache é best-effort: se falhar, os botões de gerar continuam valendo.
      } finally {
        this.insightsLoading = false;
      }
      // Garante o sentimento ao vivo (store deduplica se o chip já buscou).
      this.refreshLiveSentiment();
    },
    scheduleLiveRefresh() {
      if (this.liveTimer) clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => this.refreshLiveSentiment(), 1500);
    },
    // Sentimento ao vivo via store (fonte única, nativo/grátis, deduplicado).
    refreshLiveSentiment() {
      this.$store.dispatch('copilot/fetchLiveSentiment', {
        conversationId: this.conversationId,
        lastMessageId: this.lastMessageId,
      });
    },
    // Bloqueia uma regeração paga em cooldown (rede de segurança; a UI também
    // desabilita o botão). Retorna true se bloqueou.
    blockedByCooldown(event, force) {
      if (force && this.isCoolingDown(event)) {
        this.showAlert(
          this.$t('CONVERSATION_SIDEBAR.COPILOT.COOLDOWN_WAIT', {
            min: this.cooldownMin(event),
          })
        );
        return true;
      }
      return false;
    },
    // Detalhes (urgência, intenção, tags) — usa DeepSeek, sob demanda.
    async generateSentimentDetails(force = false) {
      if (this.blockedByCooldown('sentiment', force)) return;
      this.detailsLoading = true;
      try {
        const { data } = await CopilotAPI.processEvent({
          type: 'sentiment',
          conversationId: this.conversationId,
          force,
        });
        const message =
          data.message && typeof data.message === 'object' ? data.message : null;
        this.sentimentDetails = message;
        this.unlimited = !!data.unlimited;
        this.setCooldown('sentiment', data.cooldown_seconds);
        // O medidor ao vivo é independente (humor recente): não sobrescrevemos
        // com o sentimento da conversa inteira que vem junto dos detalhes.
        this.$set(this.staleFlags, 'sentiment', false);
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION_SIDEBAR.COPILOT.ERROR'));
      } finally {
        this.detailsLoading = false;
      }
    },
    async generateSummary(force = false) {
      if (this.blockedByCooldown('summarize', force)) return;
      this.summaryLoading = true;
      try {
        const { data } = await CopilotAPI.processEvent({
          type: 'summarize',
          conversationId: this.conversationId,
          force,
        });
        this.summary = data.message || '';
        this.unlimited = !!data.unlimited;
        this.setCooldown('summarize', data.cooldown_seconds);
        this.$set(this.staleFlags, 'summarize', false);
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION_SIDEBAR.COPILOT.ERROR'));
      } finally {
        this.summaryLoading = false;
      }
    },
    async generateSuggestions(force = false) {
      if (this.blockedByCooldown('reply_suggestion', force)) return;
      this.suggestLoading = true;
      try {
        const { data } = await CopilotAPI.processEvent({
          type: 'reply_suggestion',
          conversationId: this.conversationId,
          force,
        });
        const list = Array.isArray(data.message)
          ? data.message
          : [data.message].filter(Boolean);
        this.suggestions = list;
        this.unlimited = !!data.unlimited;
        this.setCooldown('reply_suggestion', data.cooldown_seconds);
        this.$set(this.staleFlags, 'reply_suggestion', false);
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION_SIDEBAR.COPILOT.ERROR'));
      } finally {
        this.suggestLoading = false;
      }
    },
    useSuggestion(text) {
      bus.$emit(BUS_EVENTS.SET_REPLY_EDITOR_MESSAGE, text);
      this.showAlert(this.$t('CONVERSATION_SIDEBAR.COPILOT.USED'));
    },
    async copySuggestion(text) {
      try {
        // navigator.clipboard só existe em contexto seguro (HTTPS/localhost).
        // Em HTTP puro caímos no fallback com textarea + execCommand.
        if (window.isSecureContext && navigator.clipboard) {
          await navigator.clipboard.writeText(text);
        } else {
          this.fallbackCopy(text);
        }
        this.showAlert(this.$t('CONVERSATION_SIDEBAR.COPILOT.COPIED'));
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION_SIDEBAR.COPILOT.ERROR'));
      }
    },
    fallbackCopy(text) {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      if (!ok) {
        throw new Error('copy command failed');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.copilot {
  padding: var(--space-normal);
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
  overflow-y: auto;
}

.cp-card {
  background: var(--color-surface, var(--white));
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-large);
  padding: var(--space-normal);

  &--soon {
    opacity: 0.9;
    border-style: dashed;
  }
}

.cp-card__head {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  margin-bottom: var(--space-small);

  h4 {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);
    margin: 0;
    color: var(--color-heading, var(--color-body));
  }
}

.cp-card__hint {
  font-size: var(--font-size-small);
  color: var(--s-500);
  margin: 0;
  line-height: 1.5;
}

.cp-stale {
  margin: var(--space-small) 0 0;
  font-size: var(--font-size-mini);
  color: #c77700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-micro);

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f0a020;
    flex: none;
  }
  &:hover {
    text-decoration: underline;
  }
}

// Nota de cooldown: próxima geração paga só depois de X min.
.cp-cooldown {
  margin: var(--space-small) 0 0;
  font-size: var(--font-size-mini);
  color: var(--s-500);
  display: flex;
  align-items: center;
  gap: var(--space-micro);

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--s-400);
    flex: none;
  }
}

// Selo "ilimitado" (conta usando a própria integração OpenAI).
.cp-unlimited {
  margin: 0;
  text-align: center;
  font-size: var(--font-size-mini);
  color: #1f9d57;
}

.cp-ic {
  width: 16px;
  height: 16px;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  color: var(--color-woot);

  &--ai {
    color: #7a5cf0;
    fill: currentColor;
    stroke: none;
  }
  &--sm {
    width: 13px;
    height: 13px;
  }
}

.cp-soon {
  margin-left: auto;
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: #7a5cf0;
  background: rgba(122, 92, 240, 0.12);
  padding: 2px var(--space-smaller);
  border-radius: var(--border-radius-normal);
}

.cp-refresh {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: var(--s-500);
  cursor: pointer;
  padding: var(--space-micro);
  border-radius: var(--border-radius-small);

  &:hover {
    background: var(--color-background);
    color: var(--color-woot);
  }

  // Em cooldown: apagado e sem clique.
  &--off {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background: transparent;
      color: var(--s-500);
    }
  }
}

.cp-btn {
  margin-top: var(--space-small);
  width: 100%;
  border: 0;
  cursor: pointer;
  background: var(--color-woot);
  color: var(--white);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  padding: var(--space-small);
  border-radius: var(--border-radius-normal);

  &:hover {
    filter: brightness(0.96);
  }

  // Variante secundária p/ "Gerar detalhes" (ação paga, menos destaque).
  &--ghost {
    background: transparent;
    color: var(--color-woot);
    border: 1px solid var(--color-border);

    &:hover {
      filter: none;
      background: var(--color-background);
    }
  }
}

// Indicador "ao vivo" no cabeçalho do card de sentimento.
.cp-live {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: var(--space-micro);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: #1f9d57;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.cp-live__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #1f9d57;
  animation: cp-live-pulse 1.6s ease-in-out infinite;
}

@keyframes cp-live-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.8);
  }
}

// Bloco de detalhes (pago) separado do medidor ao vivo.
.cp-sent--details {
  margin-top: var(--space-small);
  padding-top: var(--space-small);
  border-top: 1px dashed var(--color-border);
}

.cp-skeleton--details {
  margin-top: var(--space-small);
}

.cp-skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);

  span {
    height: 10px;
    border-radius: 6px;
    background: var(--color-background);
    animation: cp-pulse 1.1s ease-in-out infinite;

    &:nth-child(2) {
      width: 85%;
    }
    &:nth-child(3) {
      width: 60%;
    }
  }
}

@keyframes cp-pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

.cp-summary {
  font-size: var(--font-size-small);
  color: var(--color-body);
  line-height: 1.55;

  ::v-deep ul {
    margin: 0;
    padding-left: var(--space-normal);
  }
  ::v-deep li {
    margin-bottom: var(--space-smaller);
  }
  ::v-deep p {
    margin: 0 0 var(--space-smaller);
  }
}

.cp-sent {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
}

.cp-sent__badges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-smaller);
}

.cp-badge {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  padding: 2px var(--space-small);
  border-radius: var(--border-radius-normal);
  background: var(--color-background);
  color: var(--s-600);

  &--positive {
    background: rgba(31, 157, 87, 0.15);
    color: #1f9d57;
  }
  &--neutral {
    background: var(--color-background);
    color: var(--s-600);
  }
  &--negative {
    background: rgba(229, 72, 77, 0.15);
    color: #e5484d;
  }
  &--urg-low {
    background: var(--color-background);
    color: var(--s-500);
  }
  &--urg-medium {
    background: rgba(240, 160, 32, 0.16);
    color: #c77700;
  }
  &--urg-high {
    background: rgba(229, 72, 77, 0.15);
    color: #e5484d;
  }
}

.cp-meter {
  height: 6px;
  border-radius: 6px;
  background: var(--color-background);
  overflow: hidden;
}

.cp-meter__fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.4s ease;
  background: var(--s-400);

  &--positive {
    background: #1f9d57;
  }
  &--neutral {
    background: var(--s-400);
  }
  &--negative {
    background: #e5484d;
  }
}

.cp-sent__intent {
  font-size: var(--font-size-small);
  color: var(--color-body);
  line-height: 1.5;
  margin: 0;

  strong {
    color: var(--color-heading, var(--color-body));
  }
}

.cp-tags {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-smaller);
}

.cp-tag {
  font-size: var(--font-size-mini);
  color: var(--s-600);
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: 2px var(--space-small);
}

.cp-suggestions {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
}

.cp-sug {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small);

  p {
    font-size: var(--font-size-small);
    color: var(--color-body);
    line-height: 1.5;
    margin: 0 0 var(--space-small);
  }
}

.cp-sug__actions {
  display: flex;
  gap: var(--space-smaller);
}

.cp-chip {
  border: 1px solid var(--color-border);
  background: var(--color-surface, var(--white));
  color: var(--color-body);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  padding: var(--space-micro) var(--space-small);
  border-radius: var(--border-radius-normal);
  cursor: pointer;

  &:hover {
    background: var(--color-background);
  }
  &--primary {
    background: var(--color-woot);
    color: var(--white);
    border-color: var(--color-woot);

    &:hover {
      filter: brightness(0.96);
      background: var(--color-woot);
    }
  }
}

.cp-foot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-smaller);
  font-size: var(--font-size-mini);
  color: var(--s-400);
  text-align: center;
}
</style>
